import React, { useMemo } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Tooltip } from "ds/Tooltip";

import * as S from "./VariableLine.styles";

import type { ProjectLevelVariable, Variable } from "@packages/client";

const VariableLine = ({
  isInactive = false,
  isOverridingFrom = false,
  variable,
  url = ""
}: {
  isInactive?: boolean;
  isOverridingFrom?: boolean | string;
  variable: Variable | ProjectLevelVariable;
  url?: string;
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const labels = useMemo(() => {
    return [
      "is_json",
      "is_sensitive",
      "visible_build",
      "visible_runtime",
      "is_inheritable"
    ]
      .reduce((acc, cu) => {
        if (variable[cu])
          acc.push(
            intl.formatMessage({ id: `settings.variables.labels.${cu}` })
          );
        return acc;
      }, [] as string[])
      .join(", ");
  }, [variable, intl]);

  const isEnabled = useMemo(() => {
    if (typeof (variable as Variable).is_enabled === "undefined") return true;
    return (variable as Variable).is_enabled;
  }, [variable]);

  return (
    <S.Info
      disabled={!isEnabled || isInactive}
      id={`variable-list-${variable.name}`}
      onClick={() => {
        const path = url || `${encodeURIComponent(variable.id)}`;
        navigate(path);
      }}
      tabIndex={0}
      role="button"
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const path = url || `${encodeURIComponent(variable.id)}`;
          navigate(path);
        }
      }}
    >
      <S.Name weight="semiBold" size="small" $color="ebony">
        {variable.name}
      </S.Name>

      <S.Value className="variable-value">
        <S.Truncate>
          {variable.is_sensitive ? "*****" : variable.value}
        </S.Truncate>
      </S.Value>
      <Typography.BodyText weight="semiBold" size="extraSmall" $color="slate">
        {labels}
      </Typography.BodyText>

      <S.Badges>
        &nbsp;
        {(variable as Variable).inherited && (
          <Tooltip
            align="end"
            tooltip={intl.formatMessage({
              id: "settings.variables.inherited.details"
            })}
            analyticId="settings.variables.inherited.details"
          >
            <S.Inherited>
              {intl.formatMessage({ id: "settings.variables.inherited" })}
            </S.Inherited>
          </Tooltip>
        )}
        {!isEnabled && (
          <S.Disabled>
            {intl.formatMessage({ id: "settings.variables.disabled" })}
          </S.Disabled>
        )}
        {isOverridingFrom && (
          <Tooltip
            align="end"
            tooltip={intl.formatMessage({
              id: `settings.variables.overriding.details.${isOverridingFrom}`
            })}
            analyticId={`settings.variables.overriding.details.${isOverridingFrom}`}
          >
            <S.Overriding>
              {intl.formatMessage({ id: "settings.variables.overriding" })}
            </S.Overriding>
          </Tooltip>
        )}
        {isInactive && (
          <Tooltip
            tooltip={intl.formatMessage({
              id: "settings.variables.inactive.details"
            })}
            analyticId="settings.variables.inactive.details"
          >
            <S.Inactive>
              {intl.formatMessage({ id: "settings.variables.inactive" })}
            </S.Inactive>
          </Tooltip>
        )}
      </S.Badges>
    </S.Info>
  );
};

export default VariableLine;
